import { daysInMonth, monthName, todayArtDate } from "../lib/time"
import type { ArtDate } from "../lib/types"

interface ArtDateInputProps {
	idPrefix: string
	value: ArtDate
	onChange: (value: ArtDate) => void
}

/** Day / month / year controls for an artwork's partial date. Every part is
 *  optional, but day needs month and month needs year — the finer controls
 *  stay disabled until the coarser part is set. Each part has its own small
 *  label with a ✕ to clear it; clearing year clears everything, clearing
 *  month clears the day too.
 *  Future dates can't be picked: the year is capped at the current year, and
 *  the month/day options shrink to "so far" once the current year/month is
 *  chosen. The server enforces the same rule (artworks.go ArtDate.validate). */
export function ArtDateInput({ idPrefix, value, onChange }: ArtDateInputProps) {
	const today = todayArtDate()

	const monthCount = value.year === today.year ? today.month : 12
	const dayCount = (year: number, month: number) =>
		year === today.year && month === today.month
			? today.day
			: daysInMonth(year, month)

	const setYear = (raw: string) => {
		const year = raw === "" ? null : Number(raw)
		if (year === null || Number.isNaN(year)) {
			onChange({ year: null, month: null, day: null })
			return
		}
		// Keep the finer parts valid for the new year: a month/day that would
		// now be in the future (or past the month's length) clamps down.
		let month = value.month
		if (month !== null && year === today.year && month > today.month) {
			month = today.month
		}
		const day =
			month === null || value.day === null
				? null
				: Math.min(value.day, dayCount(year, month))
		onChange({ year, month, day })
	}

	const setMonth = (raw: string) => {
		if (raw === "" || value.year === null) {
			onChange({ ...value, month: null, day: null })
			return
		}
		const month = Number(raw)
		const day =
			value.day === null
				? null
				: Math.min(value.day, dayCount(value.year, month))
		onChange({ ...value, month, day })
	}

	const setDay = (raw: string) => {
		onChange({ ...value, day: raw === "" ? null : Number(raw) })
	}

	// The year is free-typed, so out-of-range values are only clamped on
	// blur — clamping on every keystroke would fight partial input like "20".
	const clampYearOnBlur = () => {
		if (value.year === null) return
		if (value.year > today.year) setYear(String(today.year))
		else if (value.year < 1900) setYear("1900")
	}

	// Same-height label row whether or not the clear button is showing, so
	// the three controls always align.
	const partLabel = (
		id: string,
		text: string,
		canClear: boolean,
		clear: () => void,
	) => (
		<div
			class="d-flex justify-content-between align-items-center mb-1"
			style={{ minHeight: "1.5rem" }}
		>
			<label class="form-label small text-body-secondary mb-0" for={id}>
				{text}
			</label>
			{canClear && (
				// The icon is small but the button box is 1.5rem square so it's
				// still tappable on touch screens.
				<button
					type="button"
					class="btn btn-link p-0 border-0 text-body-secondary d-flex align-items-center justify-content-center"
					style={{ width: "1.5rem", height: "1.5rem" }}
					aria-label={`Clear ${text.toLowerCase()}`}
					onClick={clear}
				>
					<i
						class="bi bi-x-circle-fill"
						style={{ fontSize: "0.9rem" }}
						aria-hidden="true"
					/>
				</button>
			)}
		</div>
	)

	return (
		<div>
			{/* Column widths: the day <select> needs room for two digits *plus*
			    Bootstrap's dropdown caret, while a 4-digit year in a plain input
			    is comfortable in less. */}
			<div class="row g-2">
				<div class="col-4">
					{partLabel(`${idPrefix}-day`, "Day", value.day !== null, () =>
						setDay(""),
					)}
					<select
						id={`${idPrefix}-day`}
						class="form-select"
						value={value.day ?? ""}
						disabled={value.month === null}
						onChange={(e) => setDay((e.target as HTMLSelectElement).value)}
					>
						<option value="">---</option>
						{value.year !== null &&
							value.month !== null &&
							Array.from(
								{ length: dayCount(value.year, value.month) },
								(_, i) => <option value={i + 1}>{i + 1}</option>,
							)}
					</select>
				</div>
				<div class="col-5">
					{partLabel(`${idPrefix}-month`, "Month", value.month !== null, () =>
						setMonth(""),
					)}
					<select
						id={`${idPrefix}-month`}
						class="form-select"
						value={value.month ?? ""}
						disabled={value.year === null}
						onChange={(e) => setMonth((e.target as HTMLSelectElement).value)}
					>
						<option value="">---</option>
						{Array.from({ length: monthCount }, (_, i) => (
							<option value={i + 1}>{monthName(i + 1)}</option>
						))}
					</select>
				</div>
				<div class="col-3">
					{partLabel(`${idPrefix}-year`, "Year", value.year !== null, () =>
						setYear(""),
					)}
					<input
						id={`${idPrefix}-year`}
						type="number"
						inputMode="numeric"
						class="form-control"
						placeholder="----"
						min={1900}
						max={today.year}
						value={value.year ?? ""}
						onInput={(e) => setYear((e.target as HTMLInputElement).value)}
						onBlur={clampYearOnBlur}
					/>
				</div>
			</div>
			<div class="form-text">Leave out whatever you don't remember.</div>
		</div>
	)
}
